import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import {
  Database, Download, RotateCcw, Trash2, Play, ShieldCheck,
  Clock, HardDrive, X, AlertTriangle, RefreshCw, Lock, History, Eye, ChevronDown, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ACTION_META = {
  created_manual: { label: 'Manual backup', color: 'text-purple-600' },
  created_auto: { label: 'Automatic backup', color: 'text-blue-600' },
  restored: { label: 'Restored', color: 'text-amber-600' },
  deleted: { label: 'Deleted', color: 'text-red-600' },
  downloaded: { label: 'Downloaded', color: 'text-emerald-600' },
  backup_failed: { label: 'Backup failed', color: 'text-red-600' },
};

const BackupManager = ({ onClose }) => {
  const { sessionToken } = useUnifiedAuth();
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [audit, setAudit] = useState([]);
  const [undoInfo, setUndoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreText, setRestoreText] = useState('');
  const [restorePreview, setRestorePreview] = useState(null);
  const [selectedCols, setSelectedCols] = useState(null); // Set of collection names, or null while loading
  const [safetySnapshot, setSafetySnapshot] = useState(true);
  const [expandedCol, setExpandedCol] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [toast, setToast] = useState(null);

  const authHeaders = { Authorization: `Bearer ${sessionToken}` };

  const notify = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, bRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/backups/status`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/admin/backups`, { headers: authHeaders }),
      ]);
      setStatus(await sRes.json());
      const b = await bRes.json();
      setBackups(b.backups || []);
      try {
        const aRes = await fetch(`${BACKEND_URL}/api/admin/backups/audit?limit=40`, { headers: authHeaders });
        const a = await aRes.json();
        setAudit(a.events || []);
      } catch { /* audit is non-critical */ }
      try {
        const uRes = await fetch(`${BACKEND_URL}/api/admin/backups/undo-status`, { headers: authHeaders });
        setUndoInfo(await uRes.json());
      } catch { /* undo status is non-critical */ }
    } catch (e) {
      notify('Failed to load backups', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/run`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      const meta = await res.json();
      notify(`Backup created — ${meta.total_documents} records, ${meta.collections_backed_up} collections`, 'success');
      await load();
    } catch {
      notify('Backup failed. Check storage connectivity.', 'error');
    } finally {
      setRunning(false);
    }
  };

  const doUndo = async () => {
    if (!window.confirm('Undo the last restore? This reinstates the safety backup that was taken automatically just before it, overwriting current data.')) return;
    setRunning(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/undo-last-restore`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      const s = await res.json();
      notify(`Undone — reinstated ${s.restored_documents} records from the safety backup`, 'success');
      await load();
    } catch {
      notify('Undo failed — no safety backup available', 'error');
    } finally {
      setRunning(false);
    }
  };

  const download = async (b) => {
    setBusyId(b.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/${b.id}/download`, { headers: authHeaders });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = b.filename || `enayahub-backup-${b.id}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      notify('Download failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const fetchPreview = async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/backups/${id}/preview?sample_size=3`, { headers: authHeaders });
    if (!res.ok) throw new Error();
    return res.json();
  };

  const openRestore = async (b) => {
    setRestoreTarget(b);
    setRestoreText('');
    setSafetySnapshot(true);
    setRestorePreview(null);
    setSelectedCols(null);
    setExpandedCol(null);
    try {
      const p = await fetchPreview(b.id);
      setRestorePreview(p);
      setSelectedCols(new Set(p.collections.map((c) => c.collection)));
    } catch {
      notify('Could not load snapshot contents', 'error');
    }
  };

  const openPreview = async (b) => {
    setPreviewTarget(b);
    setPreviewData(null);
    setExpandedCol(null);
    try {
      setPreviewData(await fetchPreview(b.id));
    } catch {
      notify('Could not load snapshot contents', 'error');
    }
  };

  const toggleCol = (name) => {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const setAllCols = (all) => {
    if (!restorePreview) return;
    setSelectedCols(all ? new Set(restorePreview.collections.map((c) => c.collection)) : new Set());
  };

  const doRestore = async () => {
    if (!restoreTarget || restoreText !== 'RESTORE') return;
    const allNames = restorePreview ? restorePreview.collections.map((c) => c.collection) : [];
    const selected = selectedCols ? Array.from(selectedCols) : [];
    if (restorePreview && selected.length === 0) { notify('Select at least one collection', 'error'); return; }
    const isAll = restorePreview && selected.length === allNames.length;
    setBusyId(restoreTarget.id);
    try {
      const body = { confirm: 'RESTORE', safety_snapshot: safetySnapshot };
      if (!isAll && selected.length) body.collections = selected;
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/${restoreTarget.id}/restore`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      const s = await res.json();
      const safeMsg = s.safety_backup_id ? ' (safety backup created)' : '';
      notify(`Restored ${s.restored_documents} records across ${s.restored_collections} collection(s)${safeMsg}`, 'success');
      setRestoreTarget(null);
      setRestoreText('');
      await load();
    } catch {
      notify('Restore failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (b) => {
    if (!window.confirm('Remove this backup from the list? The archive stays in durable storage.')) return;
    setBusyId(b.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/${b.id}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) throw new Error();
      await load();
    } catch {
      notify('Delete failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="backup-manager-modal">
      <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative min-h-screen flex items-start justify-center p-3 sm:p-6">
        <div className="relative z-10 w-full max-w-5xl bg-slate-50 rounded-2xl shadow-2xl my-4">
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-7 py-5 bg-slate-900 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Database className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-white">System Backups</h2>
                <p className="text-xs text-slate-400">Full database snapshots · every 30 minutes · durable off-database storage</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="backup-refresh-btn" title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="backup-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-5 sm:p-7">
            {/* Status cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="backup-status-cards">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Clock className="w-4 h-4" /> Schedule</div>
                <div className="text-slate-900 font-semibold">Every 30 min</div>
                <div className="text-xs text-emerald-600 mt-0.5">
                  {status?.enabled ? 'Automatic · ON' : 'Off'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><ShieldCheck className="w-4 h-4" /> Retention</div>
                <div className="text-slate-900 font-semibold">{status?.retention_days ?? 30} days</div>
                <div className={`text-xs mt-0.5 ${status?.storage_ok ? 'text-emerald-600' : 'text-red-600'}`}>
                  {status?.storage_ok ? 'Storage healthy' : 'Storage unreachable'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Database className="w-4 h-4" /> Snapshots</div>
                <div className="text-slate-900 font-semibold" data-testid="backup-total-count">{status?.total_backups ?? 0}</div>
                <div className="text-xs text-slate-400 mt-0.5">stored</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><HardDrive className="w-4 h-4" /> Storage used</div>
                <div className="text-slate-900 font-semibold">{fmtBytes(status?.total_size_bytes)}</div>
                <div className="text-xs text-slate-400 mt-0.5">Last: {fmtDate(status?.last_backup?.created_at)}</div>
              </div>
            </div>

            {/* Run now */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500 flex items-center gap-1.5">
                <Lock className="w-4 h-4 text-emerald-600 shrink-0" />
                Full snapshots (appointments, invoices, staff schedules, provider services, staff, clients…) — <strong className="text-slate-700">encrypted at rest</strong>.
              </p>
              <button
                onClick={runNow}
                disabled={running}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors shrink-0"
                data-testid="backup-run-now-btn"
              >
                {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {running ? 'Backing up…' : 'Run Backup Now'}
              </button>
            </div>

            {/* Undo last restore */}
            {undoInfo?.available && (
              <div className="flex items-center justify-between gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="undo-restore-banner">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <History className="w-4 h-4 shrink-0" />
                  A restore was performed{undoInfo.restored_by ? ` by ${undoInfo.restored_by}` : ''} on {fmtDate(undoInfo.restored_at)}. You can roll it back.
                </p>
                <button
                  onClick={doUndo}
                  disabled={running}
                  className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors shrink-0"
                  data-testid="undo-restore-btn"
                >
                  <RotateCcw className="w-4 h-4" /> Undo last restore
                </button>
              </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="backup-table">
                  <thead className="bg-slate-100 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">When</th>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-right px-4 py-3">Records</th>
                      <th className="text-right px-4 py-3">Collections</th>
                      <th className="text-right px-4 py-3">Size</th>
                      <th className="text-center px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-400">Loading…</td></tr>
                    ) : backups.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-400" data-testid="backup-empty">
                        No backups yet. The first automatic snapshot runs within 30 minutes, or click “Run Backup Now”.
                      </td></tr>
                    ) : backups.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-50" data-testid={`backup-row-${b.id}`}>
                        <td className="px-4 py-3 text-slate-800 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.trigger === 'auto' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {b.trigger === 'auto' ? 'Automatic' : 'Manual'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">{(b.total_documents || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{b.collections_backed_up || 0}</td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          <span className="inline-flex items-center gap-1 justify-end">
                            {b.encrypted && <Lock className="w-3 h-3 text-emerald-600" title="Encrypted at rest" />}
                            {fmtBytes(b.size_bytes)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openPreview(b)}
                              disabled={b.status !== 'completed'}
                              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg disabled:opacity-40 transition-colors"
                              data-testid={`backup-preview-${b.id}`} title="Preview contents"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => download(b)}
                              disabled={b.status !== 'completed' || busyId === b.id}
                              className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-40 transition-colors"
                              data-testid={`backup-download-${b.id}`} title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openRestore(b)}
                              disabled={b.status !== 'completed' || busyId === b.id}
                              className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-40 transition-colors"
                              data-testid={`backup-restore-${b.id}`} title="Restore"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => remove(b)}
                              disabled={busyId === b.id}
                              className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-40 transition-colors"
                              data-testid={`backup-delete-${b.id}`} title="Remove from list"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Activity log */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="backup-audit-log">
              <div className="flex items-center gap-2 mb-3 text-slate-700 font-medium text-sm">
                <History className="w-4 h-4" /> Activity Log
              </div>
              {audit.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="backup-audit-empty">No activity yet.</p>
              ) : (
                <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {audit.map((e) => {
                    const a = ACTION_META[e.action] || { label: e.action, color: 'text-slate-600' };
                    return (
                      <li key={e.id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2 last:border-0" data-testid={`backup-audit-${e.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`font-medium ${a.color} shrink-0`}>{a.label}</span>
                          <span className="text-slate-400 truncate">by {e.actor_email || 'system'}</span>
                        </div>
                        <span className="text-slate-400 text-xs whitespace-nowrap ml-3">{fmtDate(e.timestamp)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Restore confirmation — with selective collections + safety snapshot */}
      {restoreTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="backup-restore-modal">
          <div className="fixed inset-0 bg-slate-900/80" onClick={() => setRestoreTarget(null)}></div>
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg"><AlertTriangle className="w-6 h-6 text-amber-600" /></div>
              <h3 className="text-lg font-semibold text-slate-900">Restore this backup?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Snapshot from <strong>{fmtDate(restoreTarget.created_at)}</strong> — {(restoreTarget.total_documents || 0).toLocaleString()} records.
              Restoring <strong>overwrites the selected collections</strong> with this snapshot's data.
            </p>

            {/* Selective collection picker */}
            <div className="border border-slate-200 rounded-lg mb-4">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-t-lg">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">What to restore</span>
                {restorePreview && (
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => setAllCols(true)} className="text-emerald-600 hover:underline" data-testid="restore-select-all">All</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={() => setAllCols(false)} className="text-slate-500 hover:underline" data-testid="restore-select-none">None</button>
                  </div>
                )}
              </div>
              {!restorePreview ? (
                <div className="px-3 py-4 text-sm text-slate-400 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading contents…</div>
              ) : (
                <div className="max-h-52 overflow-y-auto p-2 space-y-0.5" data-testid="restore-collection-list">
                  {restorePreview.collections.map((c) => (
                    <div key={c.collection} className="rounded hover:bg-slate-50">
                      <div className="flex items-center gap-2 px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={selectedCols ? selectedCols.has(c.collection) : false}
                          onChange={() => toggleCol(c.collection)}
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid={`restore-col-${c.collection}`}
                        />
                        <button
                          onClick={() => setExpandedCol(expandedCol === c.collection ? null : c.collection)}
                          className="flex-1 flex items-center justify-between text-left text-sm text-slate-700"
                        >
                          <span className="flex items-center gap-1">
                            {expandedCol === c.collection ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            {c.collection}
                          </span>
                          <span className="text-xs text-slate-400">{c.count.toLocaleString()}</span>
                        </button>
                      </div>
                      {expandedCol === c.collection && (
                        <pre className="mx-2 mb-2 p-2 bg-slate-900 text-slate-100 text-[10px] rounded overflow-x-auto max-h-40" data-testid={`restore-sample-${c.collection}`}>
                          {c.samples.length ? JSON.stringify(c.samples, null, 1) : 'No sample rows.'}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Safety snapshot toggle */}
            <label className="flex items-start gap-2 mb-4 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={safetySnapshot}
                onChange={(e) => setSafetySnapshot(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="restore-safety-snapshot"
              />
              <span><ShieldCheck className="w-4 h-4 inline text-emerald-600 mr-1" />Create a <strong>safety backup</strong> of current data first (recommended — lets you undo this restore).</span>
            </label>

            <p className="text-sm text-slate-600 mb-2">Type <span className="font-mono font-bold">RESTORE</span> to confirm:</p>
            <input
              type="text"
              value={restoreText}
              onChange={(e) => setRestoreText(e.target.value)}
              placeholder="RESTORE"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-4 focus:ring-2 focus:ring-amber-500 focus:outline-none font-mono"
              data-testid="backup-restore-confirm-input"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setRestoreTarget(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium"
                data-testid="backup-restore-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doRestore}
                disabled={restoreText !== 'RESTORE' || busyId === restoreTarget.id || !restorePreview}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                data-testid="backup-restore-confirm-btn"
              >
                {busyId === restoreTarget.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Restore {selectedCols && restorePreview && selectedCols.size < restorePreview.collections.length ? `${selectedCols.size} selected` : 'now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Point-in-time preview (read-only) */}
      {previewTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="backup-preview-modal">
          <div className="fixed inset-0 bg-slate-900/80" onClick={() => setPreviewTarget(null)}></div>
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg"><Eye className="w-6 h-6 text-slate-700" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Snapshot preview</h3>
                  <p className="text-xs text-slate-400">{fmtDate(previewTarget.created_at)}</p>
                </div>
              </div>
              <button onClick={() => setPreviewTarget(null)} className="p-2 text-slate-400 hover:text-slate-700" data-testid="backup-preview-close"><X className="w-5 h-5" /></button>
            </div>
            {!previewData ? (
              <div className="py-8 text-center text-slate-400 flex items-center justify-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
              <div className="space-y-1" data-testid="backup-preview-list">
                <p className="text-sm text-slate-500 mb-2">{previewData.total_documents?.toLocaleString()} records · {previewData.collections.length} collections</p>
                {previewData.collections.map((c) => (
                  <div key={c.collection} className="border border-slate-100 rounded">
                    <button
                      onClick={() => setExpandedCol(expandedCol === c.collection ? null : c.collection)}
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <span className="flex items-center gap-1">
                        {expandedCol === c.collection ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        {c.collection}
                      </span>
                      <span className="text-xs text-slate-400">{c.count.toLocaleString()} rows</span>
                    </button>
                    {expandedCol === c.collection && (
                      <pre className="mx-2 mb-2 p-2 bg-slate-900 text-slate-100 text-[10px] rounded overflow-x-auto max-h-48">
                        {c.samples.length ? JSON.stringify(c.samples, null, 1) : 'No sample rows.'}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.type === 'error' ? 'bg-red-600' : toast.type === 'success' ? 'bg-emerald-600' : 'bg-slate-800'}`}
          data-testid="backup-toast"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default BackupManager;
